import { ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import type { CashPolicyResult } from '../engine/types'
import { formatCurrency, formatCurrencyWhole, formatPercent } from '../projectionsFormat'
import { toast } from '@/store/toastStore'

type PolicyMonthTableProps = {
  months: CashPolicyResult['months']
  lastClosedMonth: string
  lastClosedOperatingCash: number | null
  lastClosedMm: number | null
  lastClosedTotalCash: number | null
}

// `.table-forecast` base padding is far too roomy at this density, and its own `td`/`th` rules
// outrank plain utilities — hence the `!` prefixes, same lesson as the projections grid.
const CELL =
  '[&>td]:!px-2 [&>td]:!py-1 [&>td]:!align-middle [&>td]:!text-xs [&>td]:whitespace-nowrap'
const ROW_HEAD =
  '[&>th]:!px-2 [&>th]:!py-1 [&>th]:!align-middle [&>th]:!text-left [&>th]:!text-xs ' +
  '[&>th]:!font-normal [&>th]:!normal-case [&>th]:!tracking-normal [&>th]:whitespace-nowrap'
const ROW = `${CELL} ${ROW_HEAD} [&>th]:!text-gray-900 hover:bg-gray-50`
const ROW_BREACH =
  `${CELL} ${ROW_HEAD} [&>th]:!text-gray-900 ` +
  'bg-amber-50 [&>td]:!bg-amber-50 [&>th]:!bg-amber-50'
const ROW_CONTEXT =
  `${CELL} ${ROW_HEAD} [&>th]:!text-gray-500 ` +
  '[&>td]:!bg-gray-50 [&>th]:!bg-gray-50 ' +
  '[&>td]:!border-b [&>td]:!border-b-gray-300 [&>th]:!border-b [&>th]:!border-b-gray-300'

const STICKY_MONTH =
  'table-locked-edge sticky left-0 z-30 border-r border-gray-200 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]'
const STICKY_MONTH_CONTEXT = `${STICKY_MONTH} !bg-gray-50`
const STICKY_MONTH_BREACH = `${STICKY_MONTH} !bg-amber-50`

const HEAD =
  'sticky top-0 z-10 !bg-white !px-2 !py-1 !align-bottom !text-xs !font-normal !normal-case ' +
  '!tracking-normal !text-gray-600 whitespace-nowrap border-b border-b-gray-300'
const HEAD_MONTH =
  `${HEAD} table-locked-edge sticky left-0 z-40 border-r border-gray-200 !z-40 !bg-white`
const HEAD_NUM = `${HEAD} !text-right`
const NUM = 'text-right !text-xs tabular-nums text-gray-900'
const NUM_MUTED = 'text-right !text-xs tabular-nums text-gray-500'
const FLAG = 'whitespace-nowrap !text-xs text-amber-800'

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export function buildPolicyMonthTableCsv({
  months,
  lastClosedMonth,
  lastClosedOperatingCash,
  lastClosedMm,
  lastClosedTotalCash,
  initialTransfer,
}: {
  months: CashPolicyResult['months']
  lastClosedMonth: string
  lastClosedOperatingCash: number | null
  lastClosedMm: number | null
  lastClosedTotalCash: number | null
  initialTransfer: number
}): string {
  const headers = [
    'Month',
    '# Dists',
    'one_time',
    'Cash generation',
    'Distributions',
    'Sweep',
    'Interest',
    'APR',
    'Operating cash',
    'MM balance',
    'Total cash',
    'Flag',
  ]
  const rows: string[][] = [
    [
      lastClosedMonth,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      lastClosedOperatingCash == null ? '' : String(lastClosedOperatingCash),
      lastClosedMm == null ? '' : String(lastClosedMm),
      lastClosedTotalCash == null ? '' : String(lastClosedTotalCash),
      'Actual',
    ],
  ]
  if (initialTransfer !== 0) {
    rows.push([
      'Initial transfer',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      String(initialTransfer),
      '',
      '',
      'Opening MM',
    ])
  }
  for (const month of months) {
    rows.push([
      month.month,
      month.n_distributions ? String(month.n_distributions) : '',
      month.one_time_distributions > 0 ? String(month.one_time_distributions) : '',
      String(month.generation),
      String(month.distributions),
      String(month.sweep),
      String(month.interest),
      String(month.apr),
      String(month.operating_cash),
      String(month.mm_balance),
      String(month.total_cash),
      month.breach ? `Breach ${month.shortfall}` : '',
    ])
  }
  return [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n')
}

export default function PolicyMonthTable({
  months,
  lastClosedMonth,
  lastClosedOperatingCash,
  lastClosedMm,
  lastClosedTotalCash,
}: PolicyMonthTableProps) {
  const initialTransfer = months.find((month) => month.initial_transfer !== 0)?.initial_transfer ?? 0

  const copyTableCsv = async () => {
    try {
      const csv = buildPolicyMonthTableCsv({
        months,
        lastClosedMonth,
        lastClosedOperatingCash,
        lastClosedMm,
        lastClosedTotalCash,
        initialTransfer,
      })
      await navigator.clipboard.writeText(csv)
      toast.success('Copied table to clipboard')
    } catch {
      toast.error('Failed to copy table')
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="absolute -top-7 right-0 inline-flex items-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        title="Copy table as CSV"
        aria-label="Copy monthly detail table as CSV"
        onClick={() => void copyTableCsv()}
      >
        <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
      </button>
      <div className="max-h-[420px] overflow-auto rounded border border-gray-200">
        <table className="table-forecast w-full border-separate border-spacing-0 !text-xs">
          <thead>
            <tr>
              <th scope="col" className={HEAD_MONTH}>
                Month
              </th>
              <th scope="col" className={HEAD_NUM}>
                # Dists
              </th>
              <th scope="col" className={HEAD_NUM}>
                Cash generation
              </th>
              <th scope="col" className={HEAD_NUM}>
                Distributions
              </th>
              <th scope="col" className={HEAD_NUM}>
                Sweep
              </th>
              <th scope="col" className={HEAD_NUM}>
                Interest
              </th>
              <th scope="col" className={HEAD_NUM}>
                APR
              </th>
              <th scope="col" className={HEAD_NUM}>
                Operating cash
              </th>
              <th scope="col" className={HEAD_NUM}>
                MM balance
              </th>
              <th scope="col" className={HEAD_NUM}>
                Total cash
              </th>
              <th scope="col" className={HEAD}>
                Flag
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className={ROW_CONTEXT}>
              <th scope="row" className={STICKY_MONTH_CONTEXT}>{lastClosedMonth}</th>
              <td className={NUM_MUTED}>-</td>
              <td className={NUM_MUTED}>-</td>
              <td className={NUM_MUTED}>-</td>
              <td className={NUM_MUTED}>-</td>
              <td className={NUM_MUTED}>-</td>
              <td className={NUM_MUTED}>-</td>
              <td className={NUM_MUTED}>{formatCurrency(lastClosedOperatingCash)}</td>
              <td className={NUM_MUTED}>{formatCurrency(lastClosedMm)}</td>
              <td className={NUM_MUTED}>{formatCurrency(lastClosedTotalCash)}</td>
              <td className="!text-xs text-gray-500">Actual</td>
            </tr>
            {initialTransfer !== 0 ? (
              <tr className={ROW_CONTEXT}>
                <th scope="row" className={STICKY_MONTH_CONTEXT}>Initial transfer</th>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>{formatCurrencyWhole(initialTransfer)}</td>
                <td className={NUM_MUTED}>-</td>
                <td className={NUM_MUTED}>-</td>
                <td className="!text-xs text-gray-500">Opening MM</td>
              </tr>
            ) : null}
            {months.map((month) => (
              <tr key={month.t} className={month.breach ? ROW_BREACH : ROW}>
                <th scope="row" className={month.breach ? STICKY_MONTH_BREACH : STICKY_MONTH}>
                  {month.month}
                </th>
                <td
                  className={NUM}
                  title={
                    month.one_time_distributions > 0
                      ? formatCurrency(month.one_time_distributions)
                      : undefined
                  }
                >
                  {month.one_time_distributions > 0
                    ? `${month.n_distributions} + extra`
                    : month.n_distributions || '-'}
                </td>
                <td className={NUM}>{formatCurrencyWhole(month.generation)}</td>
                <td className={NUM}>{formatCurrencyWhole(month.distributions)}</td>
                <td className={NUM}>{formatCurrencyWhole(month.sweep)}</td>
                <td className={NUM}>{formatCurrencyWhole(month.interest)}</td>
                <td className={NUM}>{formatPercent(month.apr)}</td>
                <td className={NUM}>{formatCurrency(month.operating_cash)}</td>
                <td className={NUM}>{formatCurrency(month.mm_balance)}</td>
                <td className={NUM}>{formatCurrency(month.total_cash)}</td>
                <td className={FLAG}>
                  {month.breach ? `Breach ${formatCurrencyWhole(month.shortfall)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
